import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';

/**
 * Itemized cart receipt (snippets/cart-receipt.liquid). Every action is a cart
 * API call that asks for the cart sections back; the surrounding
 * cart-items-component morphs them in when the CartUpdateEvent arrives, so the
 * receipt always shows Shopify's real prices after a change.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} status
 * @property {HTMLElement} [error]
 *
 * @extends {Component<Refs>}
 */
class CartReceipt extends Component {
  requiredRefs = ['status'];

  /** @type {AbortController | null} */
  #activeRequest = null;

  /** Watches the drawer's dialog so the receipt is typed out when it opens. */
  #dialogObserver = /** @type {MutationObserver | null} */ (null);

  /** Bumped to cancel a typing run (drawer closed, receipt re-rendered). */
  #typingRun = 0;
  #typeFrame = 0;

  /**
   * The cart being typed out and how many rows are done, so a re-render
   * mid-run (the header hydrates itself after load) picks up where it was.
   */
  #typing = /** @type {{ contents: string, done: number } | null} */ (null);

  /**
   * Morph only calls updatedCallback when this element's own attributes change,
   * so while typing, watch for the text being swapped out underneath.
   */
  #rewriteObserver = new MutationObserver(() => {
    this.#rewriteObserver.disconnect();
    queueMicrotask(() => this.#maybeType());
  });

  /** Plan moves already tried (key>plan), so a failed one isn't retried in a loop. */
  #planMovesTried = new Set();
  /** @type {Promise<void> | null} */
  #movingPlans = null;

  connectedCallback() {
    super.connectedCallback();
    this.#watchDialog();
    this.#maybeType();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#movePlans();
  }

  /** Any cart change, from anywhere: check plans once the new receipt is in. */
  #handleCartUpdate = () => {
    setTimeout(() => this.#movePlans());
  };

  /**
   * Keeps subscribed lines on the plan the item-count rule gives them (see
   * snippets/cart-receipt.liquid): the single-product plan for one item, the
   * BYO plan for two or more. Lines to move carry data-normalize-plan. The new
   * sections are morphed in directly, since this isn't a customer action and
   * shouldn't reopen the drawer or animate the cart icon.
   */
  #movePlans() {
    if (this.#movingPlans || this.#activeRequest) return;
    this.#movingPlans = this.#runPlanMoves().finally(() => {
      this.#movingPlans = null;
    });
  }

  async #runPlanMoves() {

    const lines = [...this.querySelectorAll('.cart-receipt__line[data-normalize-plan]')].filter(
      (line) => line instanceof HTMLElement && !this.#planMovesTried.has(`${line.dataset.key}>${line.dataset.normalizePlan}`)
    );
    if (!lines.length) return;

    const controller = new AbortController();
    try {
      let data = null;
      for (const [index, line] of /** @type {HTMLElement[]} */ (lines).entries()) {
        this.#planMovesTried.add(`${line.dataset.key}>${line.dataset.normalizePlan}`);
        const fields = { quantity: Number(line.dataset.quantity), selling_plan: Number(line.dataset.normalizePlan) };
        data = await this.#changeLine(line, fields, index === lines.length - 1, controller.signal);
      }
      for (const [sectionId, html] of Object.entries(data?.sections ?? {})) {
        if (typeof html === 'string') await morphSection(sectionId, html);
      }
    } catch (error) {
      console.error(error);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#maybeType();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeRequest?.abort();
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
    this.#rewriteObserver.disconnect();
    this.#finishTyping();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #watchDialog() {
    this.#dialogObserver?.disconnect();
    const dialog = this.closest('dialog');
    if (!dialog || this.dataset.context !== 'drawer') return;

    this.#dialogObserver = new MutationObserver(() => {
      if (dialog.open) {
        this.#maybeType();
      } else {
        // Closing counts as seen: next time it opens fully written.
        if (this.#typing) this.#markSeen(this.#typing.contents);
        this.#typing = null;
        this.#finishTyping();
      }
    });
    this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  /**
   * Types the receipt out when it shows something new: a product or extra
   * unit not seen yet this session (in the drawer or on the cart page).
   * Seeing it again without adding anything shows it fully written. In the
   * drawer this runs from mutation and morph callbacks, before paint, so the
   * text never flashes in first.
   */
  #maybeType() {
    const dialog = this.closest('dialog');
    if (dialog && !dialog.open) return;

    /** @type {Record<string, number>} */
    const current = {};
    for (const line of this.querySelectorAll('.cart-receipt__line')) {
      if (!(line instanceof HTMLElement) || !line.dataset.variantId) continue;
      current[line.dataset.variantId] = (current[line.dataset.variantId] ?? 0) + Number(line.dataset.quantity);
    }

    const contents = JSON.stringify(current);

    /** @type {Record<string, number>} */
    let seen = {};
    try {
      seen = JSON.parse(sessionStorage.getItem(TYPED_KEY) || '{}');
    } catch {
      // No session storage (private mode): type whenever something's added.
    }

    const grew = Object.entries(current).some(([id, quantity]) => quantity > (seen[id] ?? 0));
    if (!grew || prefersReducedMotion() || /** @type {any} */ (window).Shopify?.designMode) {
      // Removals still count, so adding the item back types it again.
      this.#markSeen(contents);
      return;
    }

    const resume = this.#typing?.contents === contents ? this.#typing.done : 0;
    this.#typing = { contents, done: resume };
    this.#type(resume);
  }

  /** @param {string} contents */
  #markSeen(contents) {
    try {
      sessionStorage.setItem(TYPED_KEY, contents);
    } catch {
      // No session storage: nothing to remember.
    }
  }

  /**
   * Item lines, then each total row: tick first, then the letters.
   * @param {number} [start] - rows already typed before a re-render; shown as is
   */
  async #type(start = 0) {
    this.#finishTyping();
    const run = ++this.#typingRun;

    const blocks = [
      ...this.querySelectorAll('.cart-receipt__line'),
      ...this.querySelectorAll('.cart-receipt__totals > *'),
    ]
      .filter((block) => block instanceof HTMLElement)
      .slice(start);

    // Hide everything up front so nothing shows before its turn.
    for (const block of blocks) {
      block.classList.add('is-unwritten');
      splitLetters(block);
      for (const letter of block.querySelectorAll('.cart-receipt__letter')) letter.classList.remove('is-typed');
    }
    this.#rewriteObserver.observe(this, { childList: true, subtree: true });

    for (const block of blocks) {
      if (run !== this.#typingRun) return;
      block.classList.remove('is-unwritten');
      if (block.querySelector('.cart-receipt__tick')) await wait(TICK_DRAW_MS);
      if (run !== this.#typingRun) return;
      await this.#typeLetters([...block.querySelectorAll('.cart-receipt__letter')], run);
      if (run !== this.#typingRun) return;
      if (this.#typing) this.#typing.done++;
    }

    this.#rewriteObserver.disconnect();
    if (this.#typing) this.#markSeen(this.#typing.contents);
    this.#typing = null;
  }

  /**
   * Prints letters one at a time with a slightly uneven beat, like the set
   * builder's slip, but quicker: no row takes longer than TYPE_MAX_ROW_MS.
   * @param {Element[]} letters
   * @param {number} run
   * @returns {Promise<void>}
   */
  #typeLetters(letters, run) {
    if (!letters.length) return Promise.resolve();

    const beat = Math.max(TYPE_MIN_MS, Math.min(TYPE_BEAT_MS, TYPE_MAX_ROW_MS / letters.length));
    const times = [];
    let time = 0;
    for (const letter of letters) {
      times.push(time);
      time += beat * (0.6 + Math.random() * 0.8);
      if (letter.classList.contains('is-word-end')) time += beat * 0.5;
    }

    return new Promise((resolve) => {
      const start = performance.now();
      let shown = 0;
      /** @param {number} now */
      const step = (now) => {
        if (run !== this.#typingRun) return resolve();
        while (shown < letters.length && (times[shown] ?? 0) <= now - start) {
          letters[shown++]?.classList.add('is-typed');
        }
        if (shown < letters.length) this.#typeFrame = requestAnimationFrame(step);
        else resolve();
      };
      this.#typeFrame = requestAnimationFrame(step);
    });
  }

  /** Stops typing and shows whatever was still hidden. */
  #finishTyping() {
    this.#typingRun++;
    this.#rewriteObserver.disconnect();
    cancelAnimationFrame(this.#typeFrame);
    for (const letter of this.querySelectorAll('.cart-receipt__letter:not(.is-typed)')) {
      letter.classList.add('is-typed');
    }
    for (const block of this.querySelectorAll('.is-unwritten')) {
      block.classList.remove('is-unwritten');
    }
  }

  /** @param {Event} event */
  decrease(event) {
    const line = this.#line(event);
    if (line) this.#changeQuantity(line, Number(line.dataset.quantity) - 1);
  }

  /** @param {Event} event */
  increase(event) {
    const line = this.#line(event);
    if (line) this.#changeQuantity(line, Number(line.dataset.quantity) + 1);
  }

  /** @param {Event} event */
  remove(event) {
    const line = this.#line(event);
    if (line) this.#changeQuantity(line, 0);
  }

  /**
   * Moves the whole cart to one-time or subscription. Lines are changed one at
   * a time by key; changing a line's plan gives it a new key but leaves the
   * other lines' keys alone.
   * @param {Event} event
   */
  setPurchaseType(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const subscribe = input.value === 'subscribe';

    /** @type {{ line: HTMLElement, sellingPlan: number | null }[]} */
    const changes = [];
    for (const line of this.querySelectorAll('.cart-receipt__line')) {
      if (!(line instanceof HTMLElement)) continue;
      const subscribed = line.hasAttribute('data-subscribed');
      const planId = line.dataset.planId;

      if (subscribe && !subscribed && planId) {
        changes.push({ line, sellingPlan: Number(planId) });
      } else if (!subscribe && subscribed) {
        changes.push({ line, sellingPlan: null });
      }
    }

    this.#run(async (signal) => {
      let data = null;
      for (const [index, { line, sellingPlan }] of changes.entries()) {
        const fields = { quantity: Number(line.dataset.quantity), selling_plan: sellingPlan };
        data = await this.#changeLine(line, fields, index === changes.length - 1, signal);
      }
      return data;
    });
  }

  /** @param {Event} event */
  addUpsell(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!(button instanceof HTMLElement)) return;
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    /** @type {Record<string, unknown>} */
    const item = { id: variantId, quantity: 1 };
    if (button.dataset.planId) item.selling_plan = Number(button.dataset.planId);

    this.#run(async (signal) => {
      await this.#post(Theme.routes.cart_add_url, { items: [item] }, signal);
      // /cart/add returns only the added items, so fetch the cart and sections together.
      return this.#post(Theme.routes.cart_update_url, this.#withSections({ updates: {} }), signal);
    });
  }

  /**
   * @param {HTMLElement} line
   * @param {number} quantity
   */
  #changeQuantity(line, quantity) {
    this.#run((signal) => this.#changeLine(line, { quantity: Math.max(0, quantity) }, true, signal));
  }

  /**
   * Changes one cart line, looked up fresh. Bundler's script rewrites every
   * line's properties about a second after any cart change, and each rewrite
   * gives the line a new key; when that lands between the lookup and the
   * change, Shopify rejects the key, so look the line up again and retry.
   * @param {HTMLElement} line
   * @param {Record<string, unknown>} fields - quantity, selling_plan
   * @param {boolean} includeSections - ask for the cart sections back (last change of a batch)
   * @param {AbortSignal} signal
   */
  async #changeLine(line, fields, includeSections, signal) {
    for (let attempt = 1; ; attempt++) {
      const change = { id: await this.#currentKey(line, signal), ...fields };
      try {
        return await this.#post(Theme.routes.cart_change_url, includeSections ? this.#withSections(change) : change, signal);
      } catch (error) {
        if (!(error instanceof CartError) || attempt >= LINE_CHANGE_ATTEMPTS) throw error;
        await wait(LINE_RETRY_MS);
      }
    }
  }

  /**
   * The line's key in the cart right now. Bundler's script rewrites its line
   * properties after cart changes, which gives lines new keys after the
   * receipt has rendered, so a stale key is matched by variant and plan.
   * @param {HTMLElement} line
   * @param {AbortSignal} signal
   */
  async #currentKey(line, signal) {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { signal });
    const cart = await response.json();
    /** @type {{ key: string, variant_id: number, selling_plan_allocation?: { selling_plan: { id: number } } }[]} */
    const items = cart.items ?? [];
    if (items.some((item) => item.key === line.dataset.key)) return line.dataset.key;

    const plan = line.dataset.sellingPlan ?? '';
    const sameVariant = items.filter((item) => String(item.variant_id) === line.dataset.variantId);
    const match =
      sameVariant.find((item) => String(item.selling_plan_allocation?.selling_plan.id ?? '') === plan) ??
      // Its plan just changed (a plan move): the variant alone is enough when it's on one line.
      (sameVariant.length === 1 ? sameVariant[0] : undefined);
    return match?.key ?? line.dataset.key;
  }

  /**
   * Runs one cart action: busy state, then hands the new sections to the cart
   * components. A newer action cancels an older one still in flight.
   * @param {(signal: AbortSignal) => Promise<any>} action
   */
  async #run(action) {
    this.#activeRequest?.abort();
    const controller = new AbortController();
    this.#activeRequest = controller;

    this.setAttribute('aria-busy', 'true');
    if (this.refs.error) this.refs.error.hidden = true;
    this.refs.status.textContent = this.dataset.textUpdating || '';

    try {
      // A plan move gives lines new keys; let it land before looking lines up.
      await this.#movingPlans;
      const data = await action(controller.signal);
      if (!data) return;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.id, {
          itemCount: data.item_count,
          source: 'cart-receipt',
          sections: data.sections,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      this.#showError(error instanceof CartError ? error.message : undefined);
    } finally {
      if (this.#activeRequest === controller) {
        this.#activeRequest = null;
        this.removeAttribute('aria-busy');
        this.refs.status.textContent = '';
      }
    }
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} body
   * @param {AbortSignal} signal
   */
  async #post(url, body, signal) {
    const response = await fetch(url, { ...fetchConfig('json', { body: JSON.stringify(body) }), signal });
    const data = await response.json();
    if (!response.ok || data.status) throw new CartError(data.description || data.message);
    return data;
  }

  /**
   * Asks for every cart section on the page, so the drawer, cart page and
   * header all update from one response.
   * @param {Record<string, unknown>} body
   */
  #withSections(body) {
    const ids = new Set([this.dataset.sectionId]);
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }
    return { ...body, sections: [...ids].filter(Boolean).join(','), sections_url: window.location.pathname };
  }

  /** @param {Event} event */
  #line(event) {
    const target = event.target instanceof Element ? event.target : null;
    const line = target?.closest('.cart-receipt__line');
    return line instanceof HTMLElement ? line : null;
  }

  /** @param {string} [message] */
  #showError(message) {
    if (!this.refs.error) return;
    this.refs.error.textContent = message || this.dataset.textError || '';
    this.refs.error.hidden = false;
  }
}

class CartError extends Error {}

/** Products and quantities already typed out this session, by variant id. */
const TYPED_KEY = 'poth:cart-receipt-typed';

/** Retries for a line change rejected because Bundler re-keyed the line meanwhile. */
const LINE_CHANGE_ATTEMPTS = 3;
const LINE_RETRY_MS = 350;

/** Typewriter pace: quicker than the set builder's slip, since it replays on every new item. */
const TYPE_BEAT_MS = 14;
const TYPE_MIN_MS = 5;
const TYPE_MAX_ROW_MS = 610;
const TICK_DRAW_MS = 175;

/** @param {number} ms */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Wraps every letter in `root` in its own hidden span for the typewriter.
 * Screen readers get the text once, from a visually hidden copy.
 * Same approach as the set builder's slip (set-builder.js).
 * @param {HTMLElement} root
 */
function splitLetters(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */
  const nodes = [];
  while (walker.nextNode()) {
    const node = /** @type {Text} */ (walker.currentNode);
    if (node.data.trim() && !node.parentElement?.closest('.visually-hidden, .cart-receipt__letter')) nodes.push(node);
  }

  for (const node of nodes) {
    const spoken = document.createElement('span');
    spoken.className = 'visually-hidden';
    spoken.textContent = node.data;

    const shown = document.createElement('span');
    shown.setAttribute('aria-hidden', 'true');
    const chars = [...node.data];
    chars.forEach((char, index) => {
      if (/\s/.test(char)) {
        shown.append(char);
        return;
      }
      const letter = document.createElement('span');
      letter.className = 'cart-receipt__letter';
      if (index === chars.length - 1 || /\s/.test(chars[index + 1] ?? '')) letter.classList.add('is-word-end');
      letter.textContent = char;
      shown.append(letter);
    });

    node.replaceWith(spoken, shown);
  }
}

if (!customElements.get('cart-receipt')) {
  customElements.define('cart-receipt', CartReceipt);
}
